if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      constructor() {
        super();
        document.body.classList.add('sticky-atc-bar-enabled');

        this.selectors = {
          variantIdSelect: '[name="id"]',
        };
      }

      get quantityInput() {
        return this.querySelector('quantity-input input');
      }
      get quantity() {
        return this.querySelector('quantity-input');
      }
      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
      get productId() {
        return this.getAttribute('data-product-id');
      }
      get select() {
        return this.querySelector('select');
      }
      get productForm() {
        return this.querySelector('form[is="product-form"]');
      }

      /* O'Cloudy: resolve the main product form / product-info LIVE on every
         access instead of caching them once in connectedCallback. The
         configurator replaces <product-info> (and the form inside it) on each
         layout tile click, so a cached reference goes stale and the bar silently
         falls back to its own bare form, which cannot see the Ottoman add-on. */
      get mainProductInfo() {
        return document.querySelector(`product-info[data-product-id="${this.productId}"]`);
      }

      get productFormActions() {
        return document.querySelector(`.main-product-form[data-product-id="${this.productId}"]`);
      }

      connectedCallback() {
        this.container = this.closest('.sticky-atc-bar');
        this.submitButton = this.querySelector('[type="submit"]');
        this.selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
        const img = this.querySelector('.sticky-atc-bar__product-image img');
        this.mainImageSrc = img ? img.src : null;
        this.mainImageAlt = img ? img.alt : '';

        this.variantData = this.getVariantData();

        this.init();
        this.select.addEventListener('change', (e) => {
          if (this.isUpdating) return;
          this.isUpdating = true;

          this.updateQuantityInput();

          this.mainVariantSelects = this.mainProductInfo && this.mainProductInfo.querySelector('variant-selects');
          const selectedVariantId = this.querySelector(this.selectors.variantIdSelect).value;
          this.currentVariant = this.variantData.find((variant) => variant.id === Number(selectedVariantId));

          if (this.mainVariantSelects) {
            Array.from(this.mainVariantSelects.querySelectorAll('select, fieldset'), (element, index) => {
              const variantOptionVal = this.currentVariant.options[index];
              switch (element.tagName) {
                case 'SELECT':
                  element.value = variantOptionVal;
                  const options = element.querySelectorAll('option');
                  options.forEach((option) => option.removeAttribute('selected'));

                  element.value = variantOptionVal;
                  const selectedOption = element.querySelector(`option[value="${variantOptionVal}"]`);
                  if (selectedOption) {
                    selectedOption.setAttribute('selected', 'selected');
                  }
                  break;
                case 'FIELDSET':
                  Array.from(element.querySelectorAll('input')).forEach((radio) => {
                    if (radio.value === variantOptionVal) {
                      radio.checked = true;
                    }
                  });
                  break;
              }
            });
            setTimeout(() => {
              this.mainVariantSelects.dispatchEvent(new Event('change', { detail: { formStickty: true } }));
              this.isUpdating = false;
            }, 0);
          } else {
            this.isUpdating = false;
          }

          this.updatePrice();
          this.updateImage();
          this.updateButton(true, '', false);
          if (!this.currentVariant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!this.currentVariant.available, FoxTheme.variantStrings.soldOut);
          }
        });

        /* O'Cloudy: always route the sticky bar's Add to Cart through the MAIN
           product form so the selected add-on (Ottoman) is bundled exactly as
           it is from the in-page button. The main form is resolved on every
           click, never cached: the configurator replaces <product-info> (and
           the form inside it) on each layout swap. */
        this.submitButton.addEventListener('click', (e) => {
          const mainForm = this.productFormActions;
          if (!mainForm) return; // no main form on this page, let the bar's own form submit

          e.preventDefault();
          e.stopPropagation();

          /* Oct 18, 2026: ignore taps while a previous add is still running. */
          if (this.submitButton.classList.contains('btn--loading')) return;

          const scrollToMain = () =>
            mainForm.scrollIntoView({
              behavior: FoxTheme.config.disableAnimations ? 'auto' : 'smooth',
              block: 'center',
            });

          if (this.validateMainProductRequiredFields()) {
            /* Original theme behavior: bring the required fields into view
               before submitting so validation errors are visible. */
            scrollToMain();
            setTimeout(() => mainForm.requestSubmit(), 300);
            return;
          }

          /* Carry the quantity chosen in the sticky bar over to the main
             form so it isn't silently ignored. */
          const info = this.mainProductInfo;
          const mainQtyInput =
            mainForm.querySelector('input[name="quantity"]') ||
            (info && info.querySelector('quantity-input input'));
          if (this.quantityInput && mainQtyInput && this.quantityInput.value) {
            mainQtyInput.value = this.quantityInput.value;
            mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          /* If the main form can't submit (sold out, add-on unavailable in
             this color), show the shopper why instead of silently failing. */
          const mainSubmit = mainForm.querySelector('[type="submit"]');
          if (
            mainSubmit &&
            (mainSubmit.hasAttribute('disabled') || mainSubmit.getAttribute('aria-disabled') === 'true')
          ) {
            scrollToMain();
            return;
          }

          /* Oct 18, 2026: the add runs through the MAIN form, so the theme puts
             its spinner on the main button, which is off screen when the sticky
             bar is in use (that is the whole point of the bar). Mirror the main
             button's loading state onto the bar's button so the tap gets a
             visible reaction until the cart drawer opens or an error shows. */
          this.mirrorMainLoading(mainSubmit);

          mainForm.requestSubmit();
        });
      }

      /* Oct 18, 2026: show the theme's own spinner (btn--loading) on the sticky
         button while the main form's request is in flight. The main button gets
         btn--loading on submit and loses it in the request's finally(), on
         success and on error alike, so following that class is the most
         reliable "done" signal. Extra stops: the product-added / cart-refresh
         events, a short check in case the main form bailed before it ever
         started loading (validation), and a hard timeout. */
      mirrorMainLoading(mainSubmit) {
        const btn = this.submitButton;
        if (!btn) return;

        const stop = () => {
          btn.classList.remove('btn--loading');
          btn.removeAttribute('aria-disabled');
          if (this._loadingObserver) {
            this._loadingObserver.disconnect();
            this._loadingObserver = null;
          }
          clearTimeout(this._loadingTimeout);
          clearTimeout(this._loadingStartCheck);
          document.removeEventListener('product-ajax:added', stop);
          document.removeEventListener('cart:refresh', stop);
        };
        this._stopLoading = stop;

        btn.classList.add('btn--loading');
        btn.setAttribute('aria-disabled', 'true');

        let seenLoading = false;
        if (mainSubmit && typeof MutationObserver !== 'undefined') {
          seenLoading = mainSubmit.classList.contains('btn--loading');
          this._loadingObserver = new MutationObserver(() => {
            const isLoading = mainSubmit.classList.contains('btn--loading');
            if (isLoading) {
              seenLoading = true;
            } else if (seenLoading) {
              stop();
            }
          });
          this._loadingObserver.observe(mainSubmit, { attributes: true, attributeFilter: ['class'] });
        }

        document.addEventListener('product-ajax:added', stop);
        document.addEventListener('cart:refresh', stop);

        /* The main form returned before starting the request (required field,
           add-on mismatch, cart page mode): nothing to wait for. */
        this._loadingStartCheck = setTimeout(() => {
          if (!seenLoading && !(mainSubmit && mainSubmit.classList.contains('btn--loading'))) stop();
        }, 800);

        this._loadingTimeout = setTimeout(stop, 15000);
      }

      validateMainProductRequiredFields() {
        const mainForm = this.mainProductInfo;
        if (!mainForm) return true;
        const requiredFields = mainForm.querySelectorAll('[data-custom-property]');
        return requiredFields.length > 0;
      }

      updateQuantityInput() {
        this.currentVariant = this.variantData.find((variant) => variant.id === Number(this.selectedVariantId));
        fetch(`${this.getAttribute('data-url')}?variant=${this.currentVariant.id}&section_id=${this.sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const quantity = document.querySelector(`#QuantitySticky-${this.sectionId}`);
            const newQuantity = parsedHTML.querySelector(`#QuantitySticky-${this.sectionId}`);
            if (newQuantity) {
              quantity.innerHTML = newQuantity.innerHTML;
            }
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {});
      }

      getVariantData() {
        this.variantData =
          this.variantData || JSON.parse(this.container.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }

      init() {
        if (!this.productFormActions) {
          this.container.classList.add('sticky-atc-bar--show');
          return;
        }

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.checkDevice.bind(this);
        this.checkDevice();

        const rootMargin = `-80px 0px 0px 0px`;
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const method = entry.intersectionRatio !== 1 ? 'add' : 'remove';
              this.container.classList[method]('sticky-atc-bar--show');
            });
          },
          { threshold: 1, rootMargin }
        );
        this.setObserveTarget();
        this.syncWithMainProductForm();
      }

      setObserveTarget() {
        this.observer.observe(this.productFormActions);
        this.observeTarget = this.productFormActions;
      }

      checkDevice(e) {
        document.documentElement.style.setProperty('--sticky-atc-bar-height', this.clientHeight + 'px');
      }

      updateButton(disable = true, text, modifyClass = true) {
        const productForm = this.querySelector('#product-form-sticky-atc-bar');
        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');
        if (!addButton) return;

        const addButtonText = addButton.querySelector('span');
        if (disable) {
          addButton.setAttribute('disabled', 'disabled');
          if (text) addButtonText.textContent = text;
        } else {
          addButton.removeAttribute('disabled');
          addButtonText.textContent = FoxTheme.variantStrings.addToCart;
        }
      }

      updateImage() {
        const imageWrapper = this.querySelector('[data-variant-image]');
        if (!imageWrapper) return;
        const selected = this.querySelector('[name="id"] option:checked');
        const src = selected ? selected.getAttribute('data-variant-image') : null;
        let img = imageWrapper.querySelector('img');
        if (src) {
          if (img) {
            img.src = src;
            img.removeAttribute('srcset');
          } else {
            imageWrapper.innerHTML = `<img src="${src}" class="motion-reduce" loading="lazy" fetchpriority="low" is="image-lazy" widths="90, 180" sizes="90px">`;
          }
        } else if (this.mainImageSrc) {
          if (img) {
            img.src = this.mainImageSrc;
            img.removeAttribute('srcset');
          } else {
            imageWrapper.innerHTML = `<img src="${this.mainImageSrc}" class="motion-reduce" loading="lazy" fetchpriority="low" is="image-lazy" widths="90, 180" sizes="90px">`;
          }
        }
      }

      updatePrice() {
        const classes = {
          onSale: 'f-price--on-sale',
          soldOut: 'f-price--sold-out',
        };
        const selectors = {
          priceWrapper: '.f-price',
          salePrice: '.f-price-item--sale',
          compareAtPrice: ['.f-price-item--regular'],
          unitPriceWrapper: '.f-price__unit-wrapper',
        };
        const moneyFormat = FoxTheme.settings.moneyFormat;
        const { priceWrapper, salePrice, unitPriceWrapper, compareAtPrice } = FoxTheme.utils.queryDomNodes(
          selectors,
          this
        );
        const unitPrice = unitPriceWrapper.querySelector('.f-price__unit');

        const { compare_at_price, price, unit_price_measurement } = this.currentVariant;

        // On sale.
        if (compare_at_price && compare_at_price > price) {
          priceWrapper.classList.add(classes.onSale);
        } else {
          priceWrapper.classList.remove(classes.onSale);
        }

        // Sold out.
        if (!this.currentVariant.available) {
          priceWrapper.classList.add(classes.soldOut);
        } else {
          priceWrapper.classList.remove(classes.soldOut);
        }

        if (salePrice) salePrice.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat);

        if (compareAtPrice && compareAtPrice.length && compare_at_price > price) {
          compareAtPrice.forEach(
            (item) => (item.innerHTML = FoxTheme.Currency.formatMoney(compare_at_price, moneyFormat))
          );
        } else {
          compareAtPrice.forEach((item) => (item.innerHTML = FoxTheme.Currency.formatMoney(price, moneyFormat)));
        }

        if (unit_price_measurement && unitPrice) {
          unitPriceWrapper.classList.remove('hidden');
          const unitPriceContent = `<span>${FoxTheme.Currency.formatMoney(
            this.currentVariant.unit_price,
            moneyFormat
          )}</span>/<span data-unit-price-base-unit>${FoxTheme.Currency.getBaseUnit(this.currentVariant)}</span>`;
          unitPrice.innerHTML = unitPriceContent;
        } else {
          unitPriceWrapper.classList.add('hidden');
        }
      }

      syncWithMainProductForm() {
        FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
          const info = this.mainProductInfo;
          if (!info || event.data.sectionId !== info.dataset.section) return;
          const variant = event.data.variant;
          const variantInput = this.querySelector('[name="id"]');

          this.currentVariant = variant;
          variantInput.value = variant.id;
          this.updateImage();
          this.updatePrice();
          this.updateButton(true, '', false);
          if (!variant) {
            this.updateButton(true, '', true);
          } else {
            this.updateButton(!variant.available, FoxTheme.variantStrings.soldOut);
          }

          this.updateQuantityInput();
        });
      }
    }
  );
}
